/**
 * Contractor Type Chart Component
 * Shows distribution of hours/costs by contractor type
 */

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { PieChart, Pie, Cell } from 'recharts'
import { ChartContainer, ChartTooltip, ChartTooltipContent, ChartLegend } from '@/components/ui/chart'
import type { ChartConfig } from '@/components/ui/chart'
import { useFormatters } from '@/hooks/use-formatters'
import { ChartExportMenu } from './ChartExportMenu'
import { exportToCSV, exportToExcel, exportToPNG, generateFilename } from '@/lib/utils/export-utils'

interface WorkerTypeData {
  name: string
  value: number
  costs: number
  [key: string]: string | number // Index signature for chart compatibility
}

interface ContractorTypeChartProps {
  data?: WorkerTypeData[]
  engagementId?: string | number
}

const COLORS = ['#3b82f6', '#22c55e', '#f59e0b', '#ef4444', '#8b5cf6']

export function ContractorTypeChart({ data = [], engagementId = 0 }: ContractorTypeChartProps) {
  const { formatPercent } = useFormatters()
  
  // Transform data to include percentage in the name for legend display
  const chartData = data.map(item => ({
    ...item,
    name: `${item.name} (${formatPercent(item.value, 1)})`
  }))
  
  const chartConfig = {
    t4Worker: {
      label: "T4 Worker",
    },
    incWorker: {
      label: "Inc worker",
    },
    internal: {
      label: "Internal",
    },
    c2c: {
      label: "C2C",
    },
    t4Hourly: {
      label: "T4 Hourly",
    },
    t4Salaried: {
      label: "T4 Salaried",
    },
  } satisfies ChartConfig

  const handleExportCSV = () => {
    const exportData = data.map(item => ({
      'Worker Type': item.name,
      'Percentage': item.value,
      'Costs': item.costs,
    }))
    exportToCSV(exportData, generateFilename('ContractorType', engagementId, 'csv'))
  }

  const handleExportExcel = () => {
    const exportData = data.map(item => ({
      'Worker Type': item.name,
      'Percentage': item.value,
      'Costs': item.costs,
    }))
    exportToExcel(exportData, generateFilename('ContractorType', engagementId, 'xlsx'), 'Contractor Type')
  }

  const handleExportPNG = () => {
    exportToPNG('contractor-type-chart', generateFilename('ContractorType', engagementId, 'png'))
  }

  // Show empty state if no data
  if (!data || data.length === 0) {
    return (
      <Card id="contractor-type-chart">
        <CardHeader className="py-3 px-4">
          <div className="flex items-start justify-between">
            <div>
              <CardTitle className="text-lg">Contractor Type Distribution</CardTitle>
              <CardDescription>Costs by contractor type</CardDescription>
            </div>
            <ChartExportMenu 
              onExportCSV={handleExportCSV}
              onExportExcel={handleExportExcel}
              onExportPNG={handleExportPNG}
            />
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center h-[300px] w-full">
            <p className="text-sm text-muted-foreground">No data available</p>
          </div>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card id="contractor-type-chart">
      <CardHeader className="py-3 px-4">
        <div className="flex items-start justify-between">
          <div>
            <CardTitle className="text-lg">Contractor Type Distribution</CardTitle>
            <CardDescription>Costs by contractor type</CardDescription>
          </div>
          <ChartExportMenu 
            onExportCSV={handleExportCSV}
            onExportExcel={handleExportExcel}
            onExportPNG={handleExportPNG}
          />
        </div>
      </CardHeader>
      <CardContent>
        <ChartContainer config={chartConfig} className="h-[300px] w-full min-w-0 min-h-0">
          <PieChart>
            <Pie
              data={chartData}
              cx="50%"
              cy="50%"
              labelLine={false}
              outerRadius={80}
              fill="#8884d8"
              dataKey="value"
            >
              {chartData.map((_entry, index) => (
                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
              ))}
            </Pie>
            <ChartTooltip
              content={
                <ChartTooltipContent
                  // Pie names embed the percentage for the legend
                  // (e.g. "C2C Worker (10.2%)"); strip it for the tooltip.
                  formatter={(value, name, item) => (
                    <div className="flex w-full items-center gap-2">
                      <div
                        className="h-2.5 w-2.5 shrink-0 rounded-[2px]"
                        style={{ backgroundColor: item.payload?.fill ?? item.color }}
                      />
                      <div className="flex flex-1 items-center justify-between gap-4 leading-none">
                        <span className="text-muted-foreground">
                          {String(name).split(' (')[0]}
                        </span>
                        <span className="font-mono font-medium tabular-nums text-foreground">
                          {formatPercent(Number(value), 1)}
                        </span>
                      </div>
                    </div>
                  )}
                />
              }
            />
            <ChartLegend />
          </PieChart>
        </ChartContainer>
      </CardContent>
    </Card>
  )
}


